/*
 * Jira Ticket:
 * Created Date: Wed, 25th Mar 2020, 12:03:45 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useEffect, useState } from 'react';
import {
  Confirm,
  DateTimeInput,
  fetchStart,
  fetchEnd,
  required,
  SelectInput,
  TextInput,
  useDataProvider,
  useNotify,
  useTranslate,
  useRedirect,
} from 'react-admin';
import { useDispatch } from 'react-redux';
import { ImageArray } from 'Components';
import { TowerInspectionActionToolbar } from 'Components/Toolbars';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { columnStyles } from 'styles';
import { inspectionStatusOptions } from 'utils/choices';
import * as R from 'ramda';

import ChecklistPage from '../../Checklist/ChecklistPage';

const InspectionDetailTab = ({
  basePath,
  formValues,
  permissions,
  ...props
}) => {
  const classes = columnStyles();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmittingInspection, setIsSubmittingInspection] = useState(false);
  const [formData, setFormData] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState(false);
  const [checklistQuestions, setChecklistQuestions] = useState([]);
  const [checklistApproved, setChecklistApproved] = useState();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const { record } = props;
  const toolBarProps = {
    classes,
    setShowConfirmModal,
    isSubmittingInspection,
    setIsSubmittingInspection,
    setFormData,
    notify,
    checklistApproved,
    status: record.status,
  };

  const setLightBoxOnImgClick = index => {
    setPhotoIndex(index);
    return setIsOpen(true);
  };

  const handleUpdateTowerInspection = () => {
    setIsSubmittingInspection(true);
    dispatch(fetchStart());
    const { status, reviewerComments } = formData;
    let towerUpdateObject = {
      id: record.id,
      status, 
      reviewerComments
    }
    if(checklistQuestions.length) {
      checklistQuestions.map((question, idx) => {
        question.sequence = idx
      })
      towerUpdateObject = {...towerUpdateObject , checklistQuestions}
    }
    return dataProvider('UPDATE', 'TowerInspection', towerUpdateObject)
      .then(_ => {
        dispatch(fetchEnd());
        setShowConfirmModal(false);
        return redirect('/Inspection');
      })
      .catch(error => {
        dispatch(fetchEnd());
        closeModal();
        let code = undefined;
        let message = '';
        if (error.graphQLErrors[0]) {
          code = R.path(['extensions', 'code'], error.graphQLErrors[0]);
          message = R.path(['extensions', 'message'], error.graphQLErrors[0]);
        }
        if (code !== 'INTERNAL_SERVER_ERROR') {
          return notify(message, 'warning');
        }
        return notify('error.generic', 'warning');
      });
  };

  const closeModal = () => {
    setShowConfirmModal(false);
    return setIsSubmittingInspection(false);
  };

  useEffect(() => {
    const setImageArray = record => {
      if (record && record.images && record.images.length > 0) {
        const imageArray = record.images.map(image => image.url);
        return setImages(imageArray);
      }
    };
    setImageArray(record);
  }, [record]);

  // Based on the questions value determine whether there is a checklist & if it's wholly approved
  useEffect(() => {
    // no questions
    if (
      !checklistQuestions ||
      !Array.isArray(checklistQuestions) ||
      R.isEmpty(checklistQuestions)
    ) {
      if (checklistApproved !== undefined) {
        setChecklistApproved();
      }
      // some questions
    } else {
      const uncheckedQuestion = checklistQuestions.find(
        q => q.approved !== true
      );
      const answersValid = uncheckedQuestion === undefined;
      if (checklistApproved !== answersValid) {
        setChecklistApproved(uncheckedQuestion === undefined);
      }
    }
  }, [checklistQuestions, checklistApproved]);

  // Getting already approved questions
  async function getApprovedQuestions() {
    return dataProvider('GET_ONE', 'ApprovedInspectionDetail', {
      id: record.id,
    }).then(res => {
      if (res.data) {
        // res.data.questions.map((d) => {
        //   d.approved = true
        // })
        return res.data.questions;
      }
    });
  }

  const useMountEffect = fun => useEffect(fun, []);
  const getChecklist = () => {
    const towerTypeId = R.path(['tower', 'type', 'id'], record);
    const status = R.prop('status', record);
    dispatch(fetchStart());

    dataProvider('GET_ONE', 'TowerType', {
      id: towerTypeId,
      version: record.inspectedAt,
    })
      .then(async result => {
        const approved = await getApprovedQuestions();
        let questions = R.path(['data', 'questions'], result);
        if (approved) {
          questions = approved;
        }
        // For IN_APP_CMS_APPROVE make sure to set questions as pre-agreed
        // const checklistType = R.path(
        //   ['tower', 'site', 'project', 'checklistType'],
        //   record
        // );
        // if (checklistType === 'IN_APP_CMS_APPROVE' || status === 'APPROVED') {
        //   questions = questions.map(q => {
        //     q.approved = true;
        //     return q;
        //   });
        // }
        setChecklistQuestions(questions);
      })
      .catch(err => {
        console.log('getChecklist error', err);
        setChecklistQuestions([]);
      })
      .finally(() => {
        dispatch(fetchEnd());
      });
    return () => {};
  };

  useMountEffect(getChecklist); // run once

  const isOrganisationTower = record?.tower?.hasOwnProperty('site');
  const editable = record && !record.isExpired && isOrganisationTower
  const disableEdit = record && !record.isExpired && isOrganisationTower && record.status == 'DO_NOT_USE';
  // const editable =
  //   record &&
  //   !record.isExpired &&
  //   (record.status === 'INSPECTED' || record.status === 'APPROVED');
  const approverDefaultValue =
    record.status === 'INSPECTED'
      ? undefined
      : translate('resources.Inspection.fields.automaticallyApproved');

  const approverValue =
    R.path(['reviewedBy', 'name'], record) || approverDefaultValue;

  return (
    <>
      <div className={classes.root}>
        <div className={classes.column}>
          <TextInput
            source="inspectedBy.name"
            label="resources.Inspection.fields.inspectorName"
            disabled
          />
        </div>
        <div className={classes.column}>
          <DateTimeInput
            source="inspectedAt"
            label="resources.Inspection.fields.dateTime"
            disabled
          />
        </div>
        <div className={classes.column}>
          <SelectInput
            source="status"
            label="resources.Inspection.fields.inspectionStatus"
            disabled
            choices={inspectionStatusOptions}
          />
        </div>
        <div className={classes.column}>
          <TextInput
            source={'reviewedBy.name'}
            initialValue={approverDefaultValue}
            label="resources.Inspection.fields.approverName"
            disabled
            format={_ => {
              return approverValue;
            }}
          />
        </div>
      </div>
      <TextInput
        source="inspectorComments"
        disabled
        multiline
        fullWidth
        label="resources.Inspection.fields.inspectionComments"
      />
      <ImageArray
        label="resources.Inspection.fields.images"
        setLightBoxOnImgClick={setLightBoxOnImgClick}
        translate={translate}
        {...props}
      />
      {isOpen && images.length > 0 && (
        <Lightbox
          mainSrc={images[photoIndex]}
          nextSrc={images[(photoIndex + 1) % images.length]}
          prevSrc={images[(photoIndex + images.length - 1) % images.length]}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + images.length - 1) % images.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % images.length)
          }
        />
      )}
      {editable && (
        <ChecklistPage
          checklistQuestions={checklistQuestions}
          setChecklistQuestions={setChecklistQuestions}
          disableEdit={disableEdit}
        />
      )}
      <TextInput
        source="reviewerComments"
        disabled={editable ? isSubmittingInspection : true}
        multiline
        fullWidth
        validate={required()}
        label="resources.Inspection.fields.approverComments"
      />
      {editable ? (
        <>
          <TowerInspectionActionToolbar {...toolBarProps} />
          <Confirm
            isOpen={showConfirmModal}
            title={translate(
              'resources.Inspection.fields.updateInspectionModalTitle'
            )}
            content={translate(
              'resources.Inspection.fields.updateInspectionModalContent',
              {
                action: formData && `"${formData.status.replace(/_/g, ' ')}"`,
              }
            )}
            onConfirm={() => handleUpdateTowerInspection()}
            onClose={() => closeModal()}
          />
        </>
      ) : null}
    </>
  );
};

export default InspectionDetailTab;
